import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import FlexSearch from "flexsearch";
import { useStaticQuery, graphql, Link } from "gatsby";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import Image from "./image";
import { modifyColor } from "../utils/helpers";

const Wrapper = styled(({ searchOpen: ignored, ...rest }) => <div {...rest} />)`
  width: 100%;
  display: flex;
  position: relative;
  justify-content: flex-end;
  z-index: 999;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    justify-content: flex-start;
    flex-direction: column;
    transition: all 0.3s ease-in-out;
    margin: 0;
    transform: ${({ searchOpen }) =>
      searchOpen ? "translateY(0)" : "translateY(-100%)"};
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    padding: 0 20px;
    background-color: ${({ theme }) => theme.colors.secondary};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 0 10px;
  }
`;

const SearchIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-end;
  z-index: 1000;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-right: 10px;
  }
`;

const SearchBar = styled.div`
  margin-right: 2rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  border: 1px solid #e7e5e5;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  max-width: ${({ $searchOpen }) => ($searchOpen ? "500px" : "0")};
  opacity: ${({ $searchOpen }) => ($searchOpen ? 1 : 0)};
  transform: ${({ $searchOpen }) =>
    $searchOpen ? "translateY(0)" : "translateY(-10px)"};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    visibility 0.3s,
    max-width 0.4s ease;
  @media (hover: hover) {
    &:hover {
      box-shadow: 0 0 4px 2px rgb(0 0 0 / 4%);
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-top: 15px;
    width: 100%;
    margin-right: ${({ $searchOpen }) => ($searchOpen ? "1rem" : "0")};
    max-width: none;
  }
`;

const SearchInput = styled.input`
  padding: 15px 15px;
  width: 450px;
  border-radius: 12px;
  box-shadow: none;
  height: 50px;
  visibility: ${({ $searchOpen }) => ($searchOpen ? "visible" : "hidden")};
  border: 1px solid
    ${({ theme }) => theme.colors.headerSearch || theme.colors.primary};
  &:focus {
    outline: none;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    height: 50px;
    width: 100%;
  }
`;
const SearchButton = styled.button`
  background-color: ${({ theme }) =>
    theme.colors.headerSearch || theme.colors.primary};
  padding: 15px;
  border: none;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  border-left: 1px solid
    ${({ theme }) => theme.colors.headerSearch || theme.colors.primary};
  margin-left: -10px;
  cursor: pointer;
  transition: all 0.5s ease;
  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }) =>
        modifyColor(theme.colors.headerSearch || theme.colors.primary, -40)};
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 50px;
  }
`;
const SearchIcon = styled(FontAwesomeIcon)`
  color: ${({ theme }) => theme.colors.bodyText};
`;
const SearchResults = styled.div`
  position: absolute;
  top: 49px;
  right: 32px;
  width: 490px;
  background-color: #fff;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  max-height: 550px;
  overflow-y: scroll;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: relative;
    width: 100%;
    top: 0;
    right: 0;
    background-color: transparent;
    border: none;
  }
`;

const Result = styled(Link)`
  display: flex;
  padding: 15px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  transition: all 0.5 ease;
  color: ${({ theme }) => theme.colors.primary};
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: #efefef;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 1.3rem;
  }
`;

const SearchImage = styled(Image)`
  margin-right: 15px;
  max-height: 35px;
  min-width: 50px;
`;

const Content = styled.div`
  font-size: 0.9rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 1rem;
  }
`;

const Search = ({ staticSearchData, searchOpen, onClose }) => {
  const { localSearchPages } =
    staticSearchData ||
    useStaticQuery(graphql`
      query SearchsearchQuery {
        localSearchPages {
          publicIndexURL
          publicStoreURL
        }
      }
    `);

  const [searchIndex, setSearchIndex] = useState(null);
  const [searchStore, setSearchStore] = useState(null);
  const [shouldLoadSearch, loadSearch] = React.useReducer(() => true, false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rawResults, setRawResults] = useState(null);
  const [results, setResults] = useState(null);
  const [flexSearch, setFlexSearch] = useState(null);

  useEffect(() => {
    // Load search index
    if (shouldLoadSearch && !searchIndex) {
      fetch(localSearchPages.publicIndexURL)
        .then((response) => response.text())
        .then((index) => setSearchIndex(index));
    }
  }, [shouldLoadSearch, searchIndex]);

  useEffect(() => {
    // Load search store
    if (shouldLoadSearch && !searchStore) {
      fetch(localSearchPages.publicStoreURL)
        .then((response) => response.json())
        .then((store) => setSearchStore(store));
    }
  }, [shouldLoadSearch, searchStore]);

  useEffect(() => {
    // Create search object
    if (!flexSearch && searchIndex) {
      const newFlexSearch = FlexSearch.create();
      newFlexSearch.import(searchIndex);
      setFlexSearch(newFlexSearch);
    }
  }, [flexSearch, searchIndex]);

  useEffect(() => {
    // Do the actual search
    if (flexSearch && searchStore) {
      setRawResults(
        flexSearch.search(searchQuery).map((id) => searchStore[id])
      );
    }
  }, [searchQuery, flexSearch, searchStore]);

  useEffect(() => {
    // Process the search results returned by FlexSearch, highlight matches etc
    const sentenceRegex = new RegExp(
      "[^.,]*(" +
        searchQuery
          ?.split(/\s+/)
          .filter((i) => i?.length)
          .join("|") +
        ")[^.,]*",
      "gi"
    );

    const regex = new RegExp(
      searchQuery
        ?.split(/\s+/)
        .filter((i) => i?.length)
        .join("|"),
      "gi"
    );

    setResults(
      rawResults?.map((result) => {
        const matches = [...(result.content?.matchAll(sentenceRegex) || null)];
        return {
          title: result.title,
          id: result.id,
          uri: result.uri,
          matches: matches.map(([sentence]) =>
            sentence.replace(regex, (match) => `<strong>${match}</strong>`)
          ),
          image: result.image,
        };
      })
    );
  }, [rawResults]);

  const doSearch = (e) => {
    let onlyLetters = /^[-A-Za-z0-9\u00C0-\u00ff ]+$/.test(e);
    if (e.length >= 2 && onlyLetters) {
      setSearchQuery(e);
    } else {
      setSearchQuery("");
    }
  };

  return (
    <Wrapper searchOpen={searchOpen}>
      {!searchOpen && (
        <SearchIconWrapper onClick={() => onClose(true)}>
          <FontAwesomeIcon icon={faSearch} size="lg" />
        </SearchIconWrapper>
      )}
      <SearchBar $searchOpen={searchOpen}>
        <SearchInput
          $searchOpen={searchOpen}
          type="text"
          onChange={(event) => doSearch(event.target.value)}
          onFocus={() => {
            loadSearch();
          }}
        />
        <SearchButton onClick={() => onClose(false)}>
          <SearchIcon icon={searchOpen ? faTimes : faSearch} size="lg" />
        </SearchButton>
      </SearchBar>
      {searchOpen && (
        <>
          {results?.length > 0 && (
            <SearchResults>
              {results.map((result) => (
                <Result
                  key={result.id}
                  style={{ flexDirection: result.image ? "row" : "column" }}
                  to={result.uri}
                >
                  {result.image && <SearchImage image={result.image} />}
                  <div>
                    <h4>{result.title}</h4>
                    {result.matches.map((match, i) => (
                      <Content
                        key={i}
                        dangerouslySetInnerHTML={{ __html: match }}
                      ></Content>
                    ))}
                  </div>
                </Result>
              ))}
            </SearchResults>
          )}
        </>
      )}
    </Wrapper>
  );
};

Search.propTypes = {
  searchOpen: PropTypes.bool,
  onClose: PropTypes.func,
  staticSearchData: PropTypes.object,
};

export default Search;
